import React, { useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Link2, Loader2, Play, Check, X, AlertTriangle, ChevronDown, ChevronUp,
  Zap, BarChart3, ExternalLink, RefreshCw, CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  service: 'bg-blue-100 text-blue-700',
  pillar: 'bg-purple-100 text-purple-700',
  cluster: 'bg-teal-100 text-teal-700',
  conversion: 'bg-orange-100 text-orange-700',
};

const BulkInternalLinks = ({ onClose }) => {
  const [step, setStep] = useState('idle'); // idle, scanning, scanned, generating, generated, applying, done
  const [scanData, setScanData] = useState(null);
  const [genResults, setGenResults] = useState(null);
  const [selectedBlogs, setSelectedBlogs] = useState(new Set());
  const [expandedBlog, setExpandedBlog] = useState(null);
  const [blogSuggestions, setBlogSuggestions] = useState({});
  const [selectedLinks, setSelectedLinks] = useState({});
  const [beforeMetrics, setBeforeMetrics] = useState(null);
  const [afterMetrics, setAfterMetrics] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const filteredResults = useMemo(
    () => (genResults?.results || []).filter(r => r.new_suggestions > 0),
    [genResults]
  );

  const fetchMetrics = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/dashboard/metrics`);
      if (res.ok) return await res.json();
    } catch (err) { console.error('Metrics fetch failed:', err); }
    return null;
  };

  const scan = useCallback(async () => {
    setStep('scanning');
    const before = await fetchMetrics();
    setBeforeMetrics(before);
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/bulk/scan`);
      if (res.ok) {
        const data = await res.json();
        setScanData(data);
        // Pre-select all orphan + weak
        const ids = new Set([...data.orphan, ...data.weak].map(b => b.blog_id));
        setSelectedBlogs(ids);
        setStep('scanned');
      } else toast.error('Scan failed');
    } catch { toast.error('Network error'); setStep('idle'); }
  }, []);

  const generate = async () => {
    setStep('generating');
    const ids = [...selectedBlogs];
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/bulk/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ids),
      });
      if (res.ok) {
        const data = await res.json();
        setGenResults(data);

        // Fetch suggestions for each generated blog
        const sugMap = {};
        const linkSel = {};
        for (const r of data.results) {
          if (r.new_suggestions > 0) {
            try {
              const sRes = await fetch(`${BACKEND_URL}/api/internal-links/${r.blog_id}`);
              if (sRes.ok) {
                const sData = await sRes.json();
                sugMap[r.blog_id] = sData.internalLinks?.suggestions || [];
                // Pre-select all links
                linkSel[r.blog_id] = new Set((sData.internalLinks?.suggestions || []).map(s => s.link_id));
              }
            } catch (err) { console.error('Suggestion fetch failed:', err); }
          }
        }
        setBlogSuggestions(sugMap);
        setSelectedLinks(linkSel);
        setStep('generated');
        toast.success(`Generated suggestions for ${data.generated} blogs`);
      }
    } catch { toast.error('Generation failed'); setStep('scanned'); }
  };

  const applyAll = async () => {
    setStep('applying');
    const ids = Object.keys(blogSuggestions).filter(id => (blogSuggestions[id]?.length || 0) > 0);
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/bulk/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blog_ids: ids, mode: 'all' }),
      });
      if (res.ok) {
        const data = await res.json();
        setApplyResult(data);
        const after = await fetchMetrics();
        setAfterMetrics(after);
        setStep('done');
        toast.success(`Applied ${data.total_links_added} links to ${data.applied_blogs} blogs!`);
      }
    } catch { toast.error('Apply failed'); setStep('generated'); }
  };

  const applySelected = async () => {
    setStep('applying');
    const selections = Object.entries(selectedLinks)
      .filter(([_, ids]) => ids.size > 0)
      .map(([blogId, ids]) => ({ blog_id: blogId, link_ids: [...ids] }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/bulk/apply-selected`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selections),
      });
      if (res.ok) {
        const data = await res.json();
        setApplyResult(data);
        const after = await fetchMetrics();
        setAfterMetrics(after);
        setStep('done');
        toast.success(`Applied ${data.total_approved} links to ${data.applied_blogs} blogs!`);
      }
    } catch { toast.error('Apply failed'); setStep('generated'); }
  };

  const toggleBlogSelection = (id) => {
    setSelectedBlogs(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleLinkSelection = (blogId, linkId) => {
    setSelectedLinks(prev => {
      const blogLinks = new Set(prev[blogId] || []);
      blogLinks.has(linkId) ? blogLinks.delete(linkId) : blogLinks.add(linkId);
      return { ...prev, [blogId]: blogLinks };
    });
  };

  const isLoading = ['scanning', 'generating', 'applying'].includes(step);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-6 overflow-y-auto" data-testid="bulk-links-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mb-10">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Zap className="w-5 h-5 text-emerald-600" /> Bulk Internal Link Generation
            </h2>
            <p className="text-sm text-gray-500 mt-1">Scan, generate, and apply internal links across all published blogs</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-bulk-modal"><X className="w-4 h-4" /></Button>
        </div>

        <div className="p-6 max-h-[75vh] overflow-y-auto">
          {/* Step: Idle */}
          {step === 'idle' && (
            <div className="text-center py-12">
              <Link2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Scan & Fix Internal Links</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                This will scan all published blogs to identify orphans (no links) and weakly linked posts, 
                then generate targeted suggestions while preserving any manual editor choices.
              </p>
              <Button onClick={scan} className="bg-emerald-600 hover:bg-emerald-700" data-testid="start-scan-btn">
                <Play className="w-4 h-4 mr-2" /> Start Scan
              </Button>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-700">
                {step === 'scanning' && 'Scanning all published blogs...'}
                {step === 'generating' && 'Generating link suggestions...'}
                {step === 'applying' && 'Applying links to blogs...'}
              </p>
            </div>
          )}

          {/* Step: Scanned */}
          {step === 'scanned' && scanData && (
            <div className="space-y-6">
              {/* Scan summary */}
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: 'Total Blogs', value: scanData.total_blogs, color: 'gray' },
                  { label: 'Orphan (0 links)', value: scanData.orphan_count, color: 'red' },
                  { label: 'Weak (<2 links)', value: scanData.weak_count, color: 'amber' },
                  { label: 'Healthy (2+ links)', value: scanData.healthy_count, color: 'green' },
                ].map((s, i) => (
                  <Card key={r.blog_slug || `bulk-${i}`}>
                    <CardContent className="p-4 text-center">
                      <p className={`text-3xl font-bold text-${s.color}-600`} data-testid={`scan-${s.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>{s.value}</p>
                      <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Blog selection list */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm">Select blogs for link generation ({selectedBlogs.size} selected)</h3>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setSelectedBlogs(new Set([...scanData.orphan, ...scanData.weak].map(b => b.blog_id)))} data-testid="select-all-needy">
                      Select All Needy
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setSelectedBlogs(new Set())} data-testid="deselect-all">
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="space-y-1 max-h-72 overflow-y-auto border rounded-lg p-2">
                  {[...scanData.orphan, ...scanData.weak, ...scanData.healthy].map(b => (
                    <label key={b.blog_id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer" data-testid={`blog-select-${b.blog_id}`}>
                      <input type="checkbox" checked={selectedBlogs.has(b.blog_id)}
                        onChange={() => toggleBlogSelection(b.blog_id)}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600" />
                      <Badge className={
                        b.status === 'orphan' ? 'bg-red-100 text-red-700' :
                        b.status === 'weak' ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'
                      }>{b.status}</Badge>
                      <span className="text-sm flex-1 truncate">{b.title}</span>
                      <span className="text-[10px] text-gray-400">{b.approved_count} links</span>
                      {b.has_manual_choices && <Badge className="bg-purple-100 text-purple-700 text-[10px]">manual</Badge>}
                    </label>
                  ))}
                </div>
              </div>

              <Button onClick={generate} disabled={selectedBlogs.size === 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700 py-5" data-testid="generate-bulk-btn">
                <Zap className="w-4 h-4 mr-2" /> Generate Suggestions for {selectedBlogs.size} Blogs
              </Button>
            </div>
          )}

          {/* Step: Generated — Preview & Apply */}
          {step === 'generated' && genResults && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <Card><CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600" data-testid="gen-count">{genResults.generated}</p>
                  <p className="text-xs text-gray-500">Blogs with new suggestions</p>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{genResults.results.reduce((s, r) => s + r.new_suggestions, 0)}</p>
                  <p className="text-xs text-gray-500">Total suggestions generated</p>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-gray-400">{genResults.skipped}</p>
                  <p className="text-xs text-gray-500">Skipped (already healthy)</p>
                </CardContent></Card>
              </div>

              {/* Preview per blog */}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {filteredResults.map(r => {
                  const isOpen = expandedBlog === r.blog_id;
                  const sugs = blogSuggestions[r.blog_id] || [];
                  const selSet = selectedLinks[r.blog_id] || new Set();
                  return (
                    <Card key={r.blog_id} data-testid={`preview-${r.blog_id}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedBlog(isOpen ? null : r.blog_id)}>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate max-w-md">{r.title}</span>
                            <Badge className="bg-emerald-100 text-emerald-700">{r.new_suggestions} new</Badge>
                            {r.existing_approved > 0 && <Badge className="bg-gray-100 text-gray-600">{r.existing_approved} existing</Badge>}
                          </div>
                          {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </div>
                        {isOpen && sugs.length > 0 && (
                          <div className="mt-3 pt-3 border-t space-y-1.5">
                            {sugs.map(s => (
                              <label key={s.link_id} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer text-xs" data-testid={`link-select-${s.link_id}`}>
                                <input type="checkbox" checked={selSet.has(s.link_id)}
                                  onChange={() => toggleLinkSelection(r.blog_id, s.link_id)}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600" />
                                <Badge className={`text-[10px] px-1.5 py-0 ${TYPE_COLORS[s.link_type] || 'bg-gray-100 text-gray-600'}`}>{s.link_type}</Badge>
                                <span className="font-medium text-gray-800 truncate">"{s.anchor_text}"</span>
                                <span className="text-gray-400 flex items-center gap-0.5"><ExternalLink className="w-2.5 h-2.5" />{s.target_url}</span>
                                <span className="ml-auto text-gray-400 tabular-nums">{s.relevance_score}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="flex gap-3">
                <Button onClick={applyAll} className="flex-1 bg-emerald-600 hover:bg-emerald-700 py-5" data-testid="apply-all-btn">
                  <CheckCheck className="w-4 h-4 mr-2" /> Apply All Suggestions
                </Button>
                <Button onClick={applySelected} variant="outline" className="flex-1 border-emerald-600 text-emerald-600 py-5" data-testid="apply-selected-btn">
                  <Check className="w-4 h-4 mr-2" /> Apply Selected Only
                </Button>
              </div>
            </div>
          )}

          {/* Step: Done */}
          {step === 'done' && (
            <div className="space-y-6">
              <div className="text-center py-6">
                <Check className="w-16 h-16 text-emerald-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Bulk Linking Complete!</h3>
                {applyResult && (
                  <p className="text-gray-600">Applied <strong>{applyResult.total_links_added || applyResult.total_approved || 0}</strong> links across <strong>{applyResult.applied_blogs}</strong> blogs</p>
                )}
              </div>

              {/* Before/After Comparison */}
              {beforeMetrics && afterMetrics && (
                <Card data-testid="before-after-card">
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-600" /> Before vs After</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Before</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-gray-600">Avg Links/Post</span><span className="font-bold">{beforeMetrics.avg_links_per_post}</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Orphan Blogs</span><span className="font-bold text-red-600">{beforeMetrics.orphan_blogs}</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Total Approved</span><span className="font-bold">{beforeMetrics.total_approved_links}</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">With 2+ Links</span><span className="font-bold">{beforeMetrics.blogs_with_sufficient_links}</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Conversion %</span><span className="font-bold">{beforeMetrics.conversion_link_pct}%</span></div>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-emerald-600 uppercase mb-3">After</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-gray-600">Avg Links/Post</span>
                            <span className="font-bold text-emerald-600">{afterMetrics.avg_links_per_post}
                              {afterMetrics.avg_links_per_post > beforeMetrics.avg_links_per_post && <span className="text-xs ml-1">+{(afterMetrics.avg_links_per_post - beforeMetrics.avg_links_per_post).toFixed(1)}</span>}
                            </span>
                          </div>
                          <div className="flex justify-between"><span className="text-gray-600">Orphan Blogs</span>
                            <span className={`font-bold ${afterMetrics.orphan_blogs < beforeMetrics.orphan_blogs ? 'text-green-600' : 'text-red-600'}`}>
                              {afterMetrics.orphan_blogs}
                              {afterMetrics.orphan_blogs < beforeMetrics.orphan_blogs && <span className="text-xs ml-1">(-{beforeMetrics.orphan_blogs - afterMetrics.orphan_blogs})</span>}
                            </span>
                          </div>
                          <div className="flex justify-between"><span className="text-gray-600">Total Approved</span>
                            <span className="font-bold text-emerald-600">{afterMetrics.total_approved_links}
                              <span className="text-xs ml-1">+{afterMetrics.total_approved_links - beforeMetrics.total_approved_links}</span>
                            </span>
                          </div>
                          <div className="flex justify-between"><span className="text-gray-600">With 2+ Links</span>
                            <span className="font-bold text-emerald-600">{afterMetrics.blogs_with_sufficient_links}</span>
                          </div>
                          <div className="flex justify-between"><span className="text-gray-600">Conversion %</span>
                            <span className="font-bold text-emerald-600">{afterMetrics.conversion_link_pct}%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex gap-3">
                <Button onClick={() => { setStep('idle'); setScanData(null); setGenResults(null); setApplyResult(null); }}
                  variant="outline" className="flex-1" data-testid="run-again-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Run Again
                </Button>
                <Button onClick={onClose} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="done-close-btn">
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkInternalLinks;
